import { Link } from "react-router-dom";
import { Instagram, ArrowUpRight } from "lucide-react";
import { BUSINESS, IMAGES, INSTAGRAM_POSTS } from "../data";

export default function InstagramTeaser() {
  return (
    <section
      id="instagram"
      data-testid="instagram-teaser-section"
      className="relative py-24 sm:py-32 bg-[#0A0C10] border-t border-white/5 overflow-hidden"
    >
      {/* ambient glow */}
      <div
        aria-hidden="true"
        className="absolute -left-32 top-1/2 h-[420px] w-[420px] rounded-full bg-[#00F0FF]/12 blur-[160px]"
      />
      <div
        aria-hidden="true"
        className="absolute right-0 -bottom-20 h-[320px] w-[320px] rounded-full bg-[#00A3FF]/10 blur-[140px]"
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left — copy + CTAs */}
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#00F0FF] mb-5">
              / Instagram · {INSTAGRAM_POSTS.length} recent builds
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-white tracking-tighter leading-[1.05]">
              See every recent build,{" "}
              <span className="text-gradient-cyan">straight from the studio.</span>
            </h2>
            <p className="mt-6 max-w-xl text-[#9CA3AF] text-base leading-relaxed">
              Lambos, Mercs, M-Sports — coding jobs, ambient lighting installs,
              dashcams, security retrofits. The full archive lives on a
              dedicated page so you can scroll the lot without slowing down the
              homepage.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link
                to="/instagram"
                data-testid="instagram-page-cta"
                className="group inline-flex items-center gap-2 bg-[#00F0FF] hover:bg-[#33F3FF] text-[#0A0C10] font-semibold text-sm px-6 py-3.5 rounded-sm transition-all hover:shadow-[0_0_30px_rgba(0,240,255,0.3)]"
              >
                <Instagram className="h-4 w-4" />
                View all {INSTAGRAM_POSTS.length} posts
                <ArrowUpRight className="h-4 w-4 group-hover:translate-x-1 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <a
                href={BUSINESS.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="instagram-external-cta"
                className="inline-flex items-center gap-2 border border-white/15 text-white hover:bg-white/5 px-6 py-3.5 rounded-sm text-sm font-medium transition-colors"
              >
                Follow @autotech.uk
              </a>
            </div>

            <div className="mt-10 flex items-center gap-8 text-sm">
              <Stat label="Posts" value={INSTAGRAM_POSTS.length} />
              <Divider />
              <Stat label="Followers" value="1,150+" />
              <Divider />
              <Stat label="Updated" value="Weekly" />
            </div>
          </div>

          {/* Right — collage tiles */}
          <div className="lg:col-span-5">
            <Link
              to="/instagram"
              className="block group"
              data-testid="instagram-collage-link"
              aria-label="Open Instagram archive"
            >
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <CollageTile
                  src={IMAGES.exterior}
                  alt="Lamborghini in workshop"
                  className="aspect-[3/4]"
                />
                <CollageTile
                  src={IMAGES.dashboard}
                  alt="Modern dashboard install"
                  className="aspect-[3/4] mt-8"
                />
                <CollageTile
                  src={IMAGES.ambient}
                  alt="Ambient cabin lighting"
                  className="aspect-[3/4] -mt-2"
                />
                <CollageTile
                  src={IMAGES.dashClose}
                  alt="Digital cluster"
                  className="aspect-[3/4] mt-6"
                />
              </div>
              <div className="mt-6 inline-flex items-center gap-2 text-sm text-white/70 group-hover:text-[#00F0FF] transition-colors">
                Open the full Instagram archive
                <ArrowUpRight className="h-4 w-4" />
              </div>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function CollageTile({ src, alt, className = "" }) {
  return (
    <div
      className={`relative overflow-hidden rounded-sm border border-white/5 bg-[#12141C] ${className}`}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0C10]/90 via-transparent to-transparent" />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="leading-tight">
      <div className="font-heading text-2xl text-white">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mt-1">
        {label}
      </div>
    </div>
  );
}

function Divider() {
  return <span className="h-8 w-px bg-white/10" />;
}
